import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faArrowRight, faCircleCheck, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";

import {
	openTour,
	selectTourState,
} from "@/pages/Private/redux/onboardingTour/onboardingTour.slice";
import { OnboardingTourStatus } from "@/pages/Private/redux/onboardingTour/schema";

import {
	isTourStepComplete,
	visibleTourSteps,
} from "@/pages/Private/pages/OnboardingTour/tourSteps";

/** sessionStorage key hiding the banner for the rest of the session. */
export const TOUR_RESUME_BANNER_DISMISSED_KEY = "tourResumeBannerDismissed";

/**
 * Dashboard banner nudging users with an unfinished setup-guide tour (PREC-3700)
 * to pick up where they left off. Visible while the tour is ACTIVE or SKIPPED
 * and a step is still open or lists are waiting for the guide — the lists
 * configured in the signup wizard start when the user reaches "Check & activate"
 * (PREC-4098, PREC-4490), and until then the banner says so; once they started,
 * the API reports none waiting and the line goes. A dismiss hides it for the session.
 */
export const TourResumeBanner: FunctionComponent = () => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const tourState = useAppSelector(selectTourState);
	const { user } = useAppSelector(authSelector);
	const extensionPromoEnabled = useIsFeatureActive(FeaturesCodes.EXTENSION_PROMOTION) === true;
	const [dismissed, setDismissed] = useState(
		() => sessionStorage.getItem(TOUR_RESUME_BANNER_DISMISSED_KEY) === "1"
	);

	if (!tourState || dismissed) {
		return null;
	}

	const isResumable =
		tourState.status === OnboardingTourStatus.ACTIVE ||
		tourState.status === OnboardingTourStatus.SKIPPED;
	// Count only the steps this user still walks through — hidden steps (KB built,
	// or no sender licence) never count, and a connected CONNECT counts as done.
	const remaining = visibleTourSteps(
		tourState,
		Boolean(user?.linkedinAllowed),
		extensionPromoEnabled
	).filter((step) => !isTourStepComplete(tourState, step)).length;

	// The configured lists start on "Check & activate" (PREC-4490), so while any wait the banner
	// stays — even with every step done. The API counts none once they were started.
	const listsWaiting = tourState.listsAwaitingGuide ?? 0;

	if (!isResumable || (remaining === 0 && listsWaiting === 0)) {
		return null;
	}

	const dismiss = () => {
		sessionStorage.setItem(TOUR_RESUME_BANNER_DISMISSED_KEY, "1");
		setDismissed(true);
	};

	return (
		<div className="relative overflow-hidden bg-gradient-to-r from-brand-light to-blue-action shadow-brand-glow rounded-xl px-[18px] py-4 flex items-center gap-4 text-white">
			<span
				aria-hidden="true"
				className="pointer-events-none absolute -right-10 -top-15 w-[200px] h-[200px] rounded-full bg-white/8"
			/>
			<div className="z-1 flex items-center justify-center w-[42px] h-[42px] shrink-0 rounded-[11px] bg-white/15">
				<Icon className="w-5 h-5" icon={faCircleCheck} />
			</div>
			<div className="z-1 flex-1 min-w-0">
				{/* explicit text-white: the global `p` rule in index.css would otherwise darken it */}
				<p className="text-[15px] font-bold text-white">
					{t("onboardingTour.dashboard.resumeBanner.title")}
				</p>
				{remaining > 0 && (
					<p className="mt-px text-xs text-white/85">
						{t("onboardingTour.dashboard.resumeBanner.subtitle", { count: remaining })}
					</p>
				)}
				{listsWaiting > 0 && (
					<p className="mt-px text-xs text-white/85" data-testid="tour-lists-waiting">
						{t("onboardingTour.dashboard.resumeBanner.listsWaiting", { count: listsWaiting })}
					</p>
				)}
			</div>
			<Button
				className="z-1 shrink-0 !border-transparent !text-blue-action"
				color={ButtonColor.ACTION_SECONDARY}
				image={<Icon className="ml-2 w-3 h-3" icon={faArrowRight} />}
				iconRight
				size={ButtonSize.S}
				textClassName="font-bold"
				title={t("onboardingTour.dashboard.resumeBanner.cta")}
				type="button"
				onClick={() => dispatch(openTour(undefined))}
			/>
			<button
				aria-label={t("onboardingTour.dashboard.resumeBanner.dismiss")}
				className="z-1 cursor-pointer rounded-lg p-1 text-white/70 duration-200 hover:bg-white/15 hover:text-white"
				type="button"
				onClick={dismiss}
			>
				<Icon className="w-4 h-4" icon={faXmark} />
			</button>
		</div>
	);
};
